import { useState } from "react";
import { useTranslation } from "@i18n";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { convertDate } from "../../../../../services/luxonHelper";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import DetailsIcon from "@Components/StickySidebarViewer/assets/Details";
import UserNameTagComponent from "../../../../../cubeComponent/html/UserNameTagComponent";
import { useSelector } from "react-redux";
import { getSingleTransmittalPageUrl } from "@Features/projects/utils/projectUrls";
import { useNavigate } from "react-router-dom";
import IconComponent from "@CubeComponents/icons";
import { RootState } from "@Reducers/index";
import type { ReactNode } from "react";
import type { TranslatedString } from "@i18n";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/**
 * Transmittal stamp pushed onto a reviewer when a transmittal is sent from the review
 * (transmittals.mongo.service.ts writes `approvalStages.$[].reviewers.$[].attachedToTransmittal`
 * with exactly these three fields). It is absent from `approvalStageReviewerSchema`, so the shape the
 * tooltip below reads is declared here.
 */
type ReviewerTransmittalStamp = {
  transmittalId: string;
  transmittalIndexAtProjectLevel: number;
  createdDate: number;
};

/**
 * One reviewer of a review level. Four callers feed this component four different snapshots of the
 * same underlying record — a review's `approvalStages[].reviewers` (reviews.schema.ts), a
 * transition's `reviewerInTransition`, and `CreateReviewModal`'s own narrower view — so every field
 * is optional and at its widest wire type.
 */
type ReviewLevelTreeReviewer = {
  userId?: string;
  firstName?: string | null;
  lastName?: string | null;
  profilePic?: string | null;
  response?: string | null;
  responseTimestamp?: number | null;
  comment?: string | null;
  isUserDeleted?: boolean;
  isReviewerSelected?: boolean;
  lastNotified?: number;
  attachedToTransmittal?: readonly ReviewerTransmittalStamp[];
};

/**
 * One review level. `reviewers` is optional because `CreateReviewModal`'s `ReviewFlowLevelOption`
 * declares only `_id`/`serial`/`title`; the reads below assert it rather than guard, so the rendered
 * output is unchanged.
 */
type ReviewLevelTreeLevel = {
  _id?: string;
  title?: string;
  serial?: number;
  clearanceStatus?: string;
  levelSelectionState?: string;
  reviewers?: readonly ReviewLevelTreeReviewer[];
};

/**
 * Shape this component reads out of `timelineCircleStyleMapper`. The mapper's entries are
 * heterogeneous — `childClassName` exists only on the tick-style marks, and the three
 * `getNestedChild` implementations take unrelated arguments (a marker class name, a progress angle,
 * or nothing) — so the lookup is asserted to this common view instead of to the raw union.
 */
type TimelineCircleStyle = {
  content: string;
  childClassName?: string;
  getNestedChild: (arg?: string | number) => ReactNode;
  lineColor: string;
  circleBg: string;
  circleBorder: string;
};

interface ReviewLevelTreeProps {
  reviewLevels: readonly ReviewLevelTreeLevel[];
  showCheckboxes?: boolean;
  showProgress?: boolean;
  showNotifyBtn?: boolean;
  /**
   * Only supplied when `showCheckboxes` is set (the transition editors). The first argument is
   * `CheckBoxComponent`'s Syncfusion-shaped change event, which neither handler reads.
   */
  changeLevelCheckboxStatusHandler?: (event: unknown, levelId: string) => void;
  changeReviewerCheckboxStatusHandler?: (event: unknown, levelId: string, userId: string) => void;
  /**
   * Only supplied when `showNotifyBtn` is set (the review properties panel). The parameter types
   * mirror `notifyUserInReviewRequest`: `levelId` is a plain string there, `reviewerId` is branded.
   */
  notifyUserInReviewCallback?: (levelId: string, userId: ObjectIdString) => void;
}

function ReviewLevelTree({
  reviewLevels,
  showCheckboxes = false,
  showProgress = false,
  showNotifyBtn = false,
  changeLevelCheckboxStatusHandler,
  changeReviewerCheckboxStatusHandler,
  notifyUserInReviewCallback,
}: ReviewLevelTreeProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // `serial` is optional on the transition and CreateReviewModal variants of the level shape, but
  // every caller populates it (it is the level's 1-based order and the accordion's key). The erased
  // assertions here and at the accordion below stand in for a guard.
  const initialState: Record<number, boolean> = {};
  reviewLevels.map(({ serial }) => (initialState[serial!] = true));

  const [reviewLevelsAccState, setReviewLevelsAccState] = useState(initialState);
  const [isHovered, setIsHovered] = useState<Record<string, boolean>>({});
  const projectData = useSelector((state: RootState) => state.projectData);
  const notifyTimeOut = 86400000;

  const levelTimelineCircleMapper = {
    approved: "checkMark",
    revoked: "revokeMark",
    in_progress: "inProgressMark",
    pending: "defaultMark",
    bypassed: "bypassedMark",
  };

  const reviewerTimelineCircleMapper = {
    in_progress: {
      approve: "checkMark",
      revoke: "revokeMark",
      skip: "skippedMark",
      auto_skipped: "skippedMark",
    },
    approved: {
      approve: "checkMark",
      revoke: "revokeMark",
      skip: "skippedMark",
      auto_skipped: "skippedMark",
    },
    pending: {
      approve: "checkMark",
      revoke: "revokeMark",
      skip: "skippedMark",
      auto_skipped: "skippedMark",
    },
    revoked: {
      approve: "checkMark",
      revoke: "revokeMark",
      skip: "skippedMark",
      auto_skipped: "skippedMark",
    },
    bypassed: {
      approve: "checkMark",
      revoke: "revokeMark",
      skip: "skippedMark",
      auto_skipped: "skippedMark",
    },
  };

  const getNestedChildDiv = (childClassName?: string | number) => {
    if (childClassName === "checkMarkTick") {
      return (
        <div className="absolute start-1/2 top-1/2 h-2 w-1 -translate-x-1/2 -translate-y-1/2 rotate-45 border-b-2 border-e-2 border-white" />
      );
    }

    return <div className="absolute h-4 w-4" />;
  };

  const getInProgressChildDiv = (angle: number) => {
    let angleRect1 = 90;
    let angleRect2 = 90;
    let angleRect3 = 90;
    let angleRect4 = 90;

    if (angle > 0) {
      angleRect1 = 90 - angle > 0 ? 90 - angle : 0;
    }

    if (angle > 90) {
      angleRect2 = 180 - angle > 0 ? 180 - angle : 0;
    }

    if (angle > 180) {
      angleRect3 = 270 - angle > 0 ? 270 - angle : 0;
    }

    if (angle > 270) {
      angleRect4 = 360 - angle > 0 ? 360 - angle : 0;
    }

    const styleRect1 = { transform: `skew(0, -${angleRect1}deg)` };
    const styleRect2 = { transform: `skew(${angleRect2}deg)` };
    const styleRect3 = { transform: `skew(0, -${angleRect3}deg)` };
    const styleRect4 = { transform: `skew(${angleRect4}deg)` };

    return (
      <div className="relative z-[1] flex h-4 w-4 items-center justify-center overflow-hidden rounded-full bg-[#d0f3df]">
        <div className="absolute h-3 w-3 rounded-full bg-[#d0f3df] z-[3]" />
        <div className="absolute start-2 top-0 h-2 w-2 origin-left bg-[#45cf80] z-[2]" style={styleRect1} />
        <div className="absolute start-2 top-2 h-2 w-2 origin-top bg-[#45cf80] z-[2]" style={styleRect2} />
        <div className="absolute start-0 top-2 h-2 w-2 origin-right bg-[#45cf80] z-[2]" style={styleRect3} />
        <div className="absolute start-0 top-0 h-2 w-2 origin-bottom bg-[#45cf80] z-[2]" style={styleRect4} />
      </div>
    );
  };

  const getCrossIconChildDiv = () => (
    <div className="absolute start-0 top-0 z-[1] h-4 w-4 rotate-45">
      <div className="absolute start-0 top-0 z-[2] h-4 w-4 rounded-full bg-red-500 rotate-45" />
      <div className="absolute start-1/2 top-1/2 z-[3] h-2 w-0.5 -translate-x-1/2 -translate-y-1/2 bg-white" />
      <div className="absolute start-1/2 top-1/2 z-[3] h-0.5 w-2 -translate-x-1/2 -translate-y-1/2 bg-white" />
    </div>
  );

  const timelineCircleStyleMapper = {
    checkMark: {
      childClassName: "checkMarkTick",
      lineColor: "#45cf80",
      circleBg: "#45cf80",
      circleBorder: "#45cf80",
      content: "Submitted",
      getNestedChild: getNestedChildDiv,
    },
    inProgressMark: {
      lineColor: "#45cf80",
      circleBg: "#d0f3df",
      circleBorder: "#45cf80",
      content: "In Progress",
      getNestedChild: getInProgressChildDiv,
    },
    skippedMark: {
      lineColor: "#45cf80",
      circleBg: "#ffffff",
      circleBorder: "#45cf80",
      content: "Skipped",
      getNestedChild: getNestedChildDiv,
    },
    revokeMark: {
      lineColor: "#e75555",
      circleBg: "#e75555",
      circleBorder: "#e75555",
      content: "Revoked",
      getNestedChild: getCrossIconChildDiv,
    },
    waitingMark: {
      lineColor: "#666a84",
      circleBg: "#666a84",
      circleBorder: "#b2b5c1",
      content: "Waiting",
      getNestedChild: getNestedChildDiv,
    },
    defaultMark: {
      lineColor: "#ecf3fe",
      circleBg: "#ecf3fe",
      circleBorder: "#ecf3fe",
      content: "",
      getNestedChild: getNestedChildDiv,
    },
    bypassedMark: {
      lineColor: "#ecf3fe",
      circleBg: "#ffffff",
      circleBorder: "#ecf3fe",
      content: "Bypassed",
      getNestedChild: getNestedChildDiv,
    },
  };

  const userResponseRanking = {
    skip: 1,
    auto_skipped: 2,
    approve: 3,
    revoke: 4,
  };

  const changeAccStateHandler = (serial: number) => {
    setReviewLevelsAccState((prev: Record<number, boolean>) => ({ ...prev, [serial]: !prev[serial] }));
  };

  // The three mapper lookups below are keyed by wire strings; each read asserts the key into the
  // mapper's own key union rather than widening the mappers, which would erase their value types.
  const calculateLevelTimelineCircleType = (clearanceStatus?: string) => {
    const type = showProgress
      ? levelTimelineCircleMapper[clearanceStatus as keyof typeof levelTimelineCircleMapper]
      : null;
    return type;
  };

  const calculateReviewerTimelineCircleType = (clearanceStatus?: string, response?: string | null) => {
    let type: string | undefined;

    if (response) {
      type =
        reviewerTimelineCircleMapper[clearanceStatus as keyof typeof reviewerTimelineCircleMapper]?.[
          response as keyof (typeof reviewerTimelineCircleMapper)["approved"]
        ];
    } else if ((["in_progress"] as (string | undefined)[]).includes(clearanceStatus)) {
      type = "waitingMark";
    } else if ((["pending", "revoked"] as (string | undefined)[]).includes(clearanceStatus)) {
      type = "defaultMark";
    } else if ((["bypassed"] as (string | undefined)[]).includes(clearanceStatus)) {
      type = "bypassedMark";
    }

    return showProgress ? type : null;
  };

  const getUserAvatarComponent = (user: ReviewLevelTreeReviewer) => <UserNameTagComponent id={user.userId} />;

  // KNOWN BUG, preserved verbatim: the three callbacks below are only passed when the matching
  // `showCheckboxes` / `showNotifyBtn` flag is set, and nothing here re-checks that pairing — a caller
  // that sets the flag without the handler would throw. Erased assertions; a guard would change
  // behaviour. The `_id`/`userId` assertions cover the same gap: both are optional on some of the
  // level shapes this component accepts, but the transition editors that own these handlers always
  // send them.
  const getReviewerCheckbox = (_id?: string, userId?: string, isReviewerSelected?: boolean) => (
    <div className="flex h-4 w-4 items-center justify-center">
      <CheckBoxComponent
        checked={isReviewerSelected}
        onChange={(e) => changeReviewerCheckboxStatusHandler!(e, _id!, userId!)}
      />
    </div>
  );

  const getLevelCheckbox = (_id?: string, levelSelectionState?: string) => {
    const isChecked = levelSelectionState === "complete";
    const isPartial = levelSelectionState === "partial";

    // This checkbox sits inside `AccordionSummary`, whose whole row toggles the accordion — without
    // stopping the click here, ticking a level collapses the level being ticked.
    return (
      <div className="flex h-4 w-4 items-center justify-center" onClick={(event) => event.stopPropagation()}>
        {isPartial ? (
          <CheckBoxComponent indeterminate={isPartial} onChange={(e) => changeLevelCheckboxStatusHandler!(e, _id!)} />
        ) : (
          <CheckBoxComponent checked={isChecked} onChange={(e) => changeLevelCheckboxStatusHandler!(e, _id!)} />
        )}
      </div>
    );
  };

  const getTimelineCircle = (
    type: string | null | undefined,
    levelIndex: number | undefined,
    reviewerIndex: number,
    progressAngle: number | undefined,
  ) => {
    const zIndex = parseInt(`${levelIndex}${reviewerIndex}`);
    type = type || "defaultMark";
    const { content, childClassName, getNestedChild, lineColor, circleBg, circleBorder } = timelineCircleStyleMapper[
      type as keyof typeof timelineCircleStyleMapper
    ] as TimelineCircleStyle;

    const args = type === "inProgressMark" ? progressAngle : childClassName;
    const timelineContent = (
      <div className="relative h-4 w-4" style={{ zIndex }}>
        <div
          className="absolute start-[0.375rem] top-full h-[62.4375rem] w-[0.1875rem] border-s-2"
          style={{ borderColor: lineColor }}
        />
        <div
          className="absolute inset-0 rounded-full border-2"
          style={{ backgroundColor: circleBg, borderColor: circleBorder }}
        />
        <div className="relative z-[1] flex h-4 w-4 items-center justify-center">
          <div className="h-4 w-4">{getNestedChild(args)}</div>
        </div>
      </div>
    );

    // i18n GAP: `timelineCircleStyleMapper.content` holds bare English literals ("Submitted",
    // "In Progress", …) that are never run through `t()`/`untranslated()`. Erased assertion below;
    // wrapping them would emit a call and change behaviour.
    if (content) {
      return (
        <>
          <ToolTipComponent showTipPointer position="left" content={content as TranslatedString}>
            {timelineContent}
          </ToolTipComponent>
        </>
      );
    }

    return <>{timelineContent}</>;
  };

  const header = (
    _id: string | undefined,
    serial: number | undefined,
    title: string | undefined,
    type: string | null | undefined,
    clearanceStatus: string | undefined,
    levelSelectionState: string | undefined,
    progressAngle: number,
  ) => (
    <div className="flex items-center justify-start gap-2.5 pb-4 ps-4">
      {!showCheckboxes ? getTimelineCircle(type, serial, 0, progressAngle) : getLevelCheckbox(_id, levelSelectionState)}
      <span className={clearanceStatus === "pending" && showProgress ? "font-semibold opacity-30" : "font-semibold"}>
        {`Level ${serial}`}
      </span>
      <span className={clearanceStatus === "pending" && showProgress ? "opacity-30" : undefined}>{title}</span>
    </div>
  );

  const compareUserResponsesMapper = (a: ReviewLevelTreeReviewer, b: ReviewLevelTreeReviewer) => {
    const priorityA = a.response ? userResponseRanking[a.response as keyof typeof userResponseRanking] : 5;
    const priorityB = b.response ? userResponseRanking[b.response as keyof typeof userResponseRanking] : 5;

    return priorityA - priorityB;
  };

  const getTooltipContent = (
    clearanceStatus: string | undefined,
    response: string | null | undefined,
    lastNotified: number | undefined,
    currentTimestamp: number,
  ): TranslatedString | undefined => {
    if (clearanceStatus !== "in_progress") {
      return t("The user belongs to a level which is not in progress");
    }

    if (response) {
      return t("The user has already submitted their response");
    }

    if (lastNotified && currentTimestamp - lastNotified < notifyTimeOut) {
      return t("You can notify this user again after {{hours, number}} hours", { hours: 24 });
    }
  };

  const handleOpenTransmittalLink = (lastTransmittal: ReviewerTransmittalStamp) => () => {
    const { transmittalId } = lastTransmittal;
    const projectId = projectData._id;

    if (!projectId) {
      return;
    }

    const singleTransmittalPageURL = getSingleTransmittalPageUrl(projectId, transmittalId);

    if (singleTransmittalPageURL) {
      navigate(singleTransmittalPageURL);
    }
  };

  const content = (
    _id: string | undefined,
    reviewers: readonly ReviewLevelTreeReviewer[],
    clearanceStatus: string | undefined,
    serial: number | undefined,
  ) => {
    const sortedReviewers = [...reviewers];
    if (showProgress) {
      sortedReviewers.sort(compareUserResponsesMapper);
    }

    const currentTimestamp = Date.now();
    // Timeline mode must stay at `ps-4`: there the level and reviewer marks share one continuous
    // vertical line that indenting would break. 2.625rem aligns a reviewer with the level title
    // (the header's `ps-4` + the level checkbox's `w-4` + the `gap-2.5` between them).
    return (
      <div className={showCheckboxes ? "flex flex-col gap-5 ps-[2.625rem]" : "flex flex-col gap-5 ps-4"}>
        {sortedReviewers.map((reviewer: ReviewLevelTreeReviewer, index: number) => {
          const {
            userId,
            response,
            isReviewerSelected,
            responseTimestamp,
            isUserDeleted,
            comment,
            lastNotified,
            attachedToTransmittal,
            firstName,
            lastName,
          } = reviewer;
          const type = calculateReviewerTimelineCircleType(clearanceStatus, response);

          const lastTransmittal = attachedToTransmittal?.at(-1);

          const enableNotifyBtn =
            (!lastNotified || currentTimestamp - lastNotified >= notifyTimeOut) &&
            clearanceStatus === "in_progress" &&
            !response;
          const tooltipText = enableNotifyBtn
            ? t("Notify User")
            : getTooltipContent(clearanceStatus, response, lastNotified, currentTimestamp);

          return (
            <div
              key={index}
              className="flex flex-col gap-3"
              onMouseEnter={() =>
                setIsHovered((prev: Record<string, boolean>) => ({
                  ...prev,
                  [`${_id}-${userId}`]: true,
                }))
              }
              onMouseLeave={() =>
                setIsHovered((prev: Record<string, boolean>) => ({
                  ...prev,
                  [`${_id}-${userId}`]: false,
                }))
              }
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center justify-start gap-2.5 flex-1 min-w-0">
                  {!showCheckboxes
                    ? getTimelineCircle(type, serial, index + 1, undefined)
                    : getReviewerCheckbox(_id, userId, isReviewerSelected)}
                  <div
                    className={
                      clearanceStatus === "pending" && showProgress
                        ? "flex items-center gap-2.5 opacity-30 flex-1 min-w-0"
                        : "flex items-center gap-2.5 flex-1 min-w-0"
                    }
                  >
                    <span
                      className="text-base font-semibold overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                      data-testid="userProfileDetail"
                    >
                      {getUserAvatarComponent(reviewer)}
                    </span>

                    {isUserDeleted && (
                      <ToolTipComponent
                        showTipPointer
                        position="bottom"
                        content={t("The reviewer has been removed from this space")}
                      >
                        <span className="flex items-center">
                          <DetailsIcon size="24" color="#FF0000" />
                        </span>
                      </ToolTipComponent>
                    )}
                    {lastTransmittal ? (
                      <IconComponent
                        position="bottom"
                        toolTipContent={t(
                          "Transmittal TS-{{transmittalIndex}} sent by {{firstName}} {{lastName}}on {{date}}",
                          {
                            transmittalIndex: lastTransmittal.transmittalIndexAtProjectLevel,
                            firstName,
                            lastName: lastName ? `${lastName} ` : "",
                            date: convertDate(Number(lastTransmittal.createdDate), "dateForText"),
                          },
                        )}
                        className="cursor-pointer"
                        name="TransmittalIcon"
                        width="1.5rem"
                        height="1.5rem"
                        onClick={handleOpenTransmittalLink(lastTransmittal)}
                      />
                    ) : undefined}
                    {showNotifyBtn && isHovered[`${_id}-${userId}`] ? (
                      <IconComponent
                        position="bottom"
                        toolTipContent={tooltipText}
                        isEnabled={enableNotifyBtn}
                        onClick={() => notifyUserInReviewCallback!(_id!, userId as ObjectIdString)}
                        name="BellIcon"
                        width="1.5rem"
                        height="1.5rem"
                      />
                    ) : undefined}
                  </div>
                </div>
                {responseTimestamp && (
                  <span className="text-xs font-normal flex-shrink-0">
                    {convertDate(Number(responseTimestamp), "dateForText")}
                  </span>
                )}
              </div>
              {comment && (["approve", "revoke", "skip"] as (string | null | undefined)[]).includes(response) && (
                <p className="ms-[1.875rem] rounded bg-[#f0f5f9] p-2 text-sm">{comment}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="relative overflow-y-hidden pt-0">
      {reviewLevels.map((reviewLevel: ReviewLevelTreeLevel, index: number) => {
        const { _id, title, serial, reviewers, clearanceStatus, levelSelectionState } = reviewLevel;
        const type = calculateLevelTimelineCircleType(clearanceStatus);
        const className = "!shadow-none !static";
        let angle = 360;

        // `reviewers` is undeclared on `CreateReviewModal`'s level shape (see `ReviewLevelTreeLevel`),
        // but every caller that renders progress or reviewer rows sends it. Erased assertions.
        if (showProgress) {
          const totalReviewers = reviewers!.length;
          const reviewersWithResponse = reviewers!.filter(
            (reviewer: ReviewLevelTreeReviewer) => reviewer.response,
          ).length;

          const ratio = reviewersWithResponse / totalReviewers;
          angle = ratio * 360;
        }

        return (
          <Accordion
            key={index}
            disableGutters
            expanded={reviewLevelsAccState[serial!]}
            onChange={() => changeAccStateHandler(serial!)}
            className={className}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className="!static">
              {header(_id, serial, title, type, clearanceStatus, levelSelectionState, angle)}
            </AccordionSummary>
            <AccordionDetails className="!pe-0 !pt-0 !pb-4">
              {content(_id, reviewers!, clearanceStatus, serial)}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
}

export default ReviewLevelTree;
